import { createTheme, PaletteMode } from '@mui/material';
import { enUS, esES, frFR, ptBR } from '@mui/material/locale';
import { grey } from '@mui/material/colors';
import '@mui/x-date-pickers/themeAugmentation';
import { SupportedLanguages } from './i18n/translations';

// DESIGN TOKENS
// All color and size values live here. No hardcoded values elsewhere.
const TOKENS = {
  // Primary
  PRIMARY_MAIN: '#1AB0B8',
  PRIMARY_LIGHT_LIGHT: '#DAF4F3',
  PRIMARY_LIGHT_DARK: '#203A3B',

  // Error
  ERROR_MAIN_LIGHT: '#bf172b',
  ERROR_LIGHT_LIGHT: '#FAC8CE',
  ERROR_MAIN_DARK: '#D61A30',
  ERROR_LIGHT_DARK: 'rgba(78,8,16,0.7)',

  // Success
  SUCCESS_MAIN_LIGHT: '#75BF57',
  SUCCESS_LIGHT_LIGHT: '#D2FBC1',
  SUCCESS_MAIN_DARK: '#84C669',
  SUCCESS_LIGHT_DARK: 'rgba(28,86,5,0.7)',

  // Warning (same for both modes)
  WARNING_MAIN_LIGHT: '#B26A00',
  WARNING_LIGHT_LIGHT: '#FFE8A3',
  WARNING_MAIN_DARK: '#F5A623',
  WARNING_LIGHT_DARK: 'rgba(102, 60, 0, 0.7)',

  // Background
  BG_DEFAULT_LIGHT: '#F5F5F5',
  BG_PAPER_LIGHT: '#FFFFFF',
  BG_DEFAULT_DARK: '#1C1A19',
  BG_PAPER_DARK: '#2F2D2C',

  // Custom-only tokens
  PRIMARY_LIGHT_LIGHT_TEXT: '333333',
  TABLE_HEADER_BG_LIGHT: '#E0E0E0',
  TABLE_FONT_SIZE_PX: 12,
  TABLE_HEADER_FONT_SIZE_PX: 13,

  // concDetErrorType map
  CONCDET_EXISTS_IN_FV_NOT_IN_FISCAL: '#FEBF01',
  CONCDET_EXISTS_IN_FISCAL_NOT_IN_FV: '#1ECAD3',
  CONCDET_DIFFERENTS_STATUS: '#80BC00',
  CONCDET_DIFFERENTS_CNPJ: '#F18A00',
  CONCDET_DIFFERENTS_AMOUNTS: '#752157',
  CONCDET_DIFFERENTS_POS_AMOUNT: '#0B6B63',
  CONCDET_POS_NOT_IN_FV: '#BF172B',
  CONCDET_DIFFERENTS_POS_GCS: '#4A148C',
  CONCDET_POS_NOT_IN_FISCAL: '#80BC00',

  // Status (columna Status) — COLORES PROPIOS
  STATUS_OK_BG_LIGHT: '#D2FBC1',
  STATUS_OK_FG_LIGHT: '#75BF57',
  STATUS_OK_BG_DARK: '#BADDAC',
  STATUS_OK_FG_DARK: '#262422',

  STATUS_ERR_BG_LIGHT: '#FAC8CE',
  STATUS_ERR_FG_LIGHT: '#bf172b',
  STATUS_ERR_BG_DARK: '#CF6679',
  STATUS_ERR_FG_DARK: '#262422',

  // Pagination Number
  PAGE_ACTIVE_LIGHT: '#0B6B63',
  PAGE_ACTIVE_DARK: '#0B6B63',
};

const muiLocal: Record<SupportedLanguages, object[]> = {
  fr: [frFR],
  es: [esES],
  en: [enUS],
  pt: [ptBR],
};

// MUI Vars Strategies
const buildMuiPalette = (mode: PaletteMode) => {
  const isDark = mode === 'dark';
  return {
    primary: {
      main: TOKENS.PRIMARY_MAIN,
      light: isDark ? TOKENS.PRIMARY_LIGHT_DARK : TOKENS.PRIMARY_LIGHT_LIGHT,
    },
    error: {
      main: isDark ? TOKENS.ERROR_MAIN_DARK : TOKENS.ERROR_MAIN_LIGHT,
      light: isDark ? TOKENS.ERROR_LIGHT_DARK : TOKENS.ERROR_LIGHT_LIGHT,
    },
    success: {
      main: isDark ? TOKENS.SUCCESS_MAIN_DARK : TOKENS.SUCCESS_MAIN_LIGHT,
      light: isDark ? TOKENS.SUCCESS_LIGHT_DARK : TOKENS.SUCCESS_LIGHT_LIGHT,
    },
    warning: {
      main: isDark ? TOKENS.WARNING_MAIN_DARK : TOKENS.WARNING_MAIN_LIGHT,
      light: isDark ? TOKENS.WARNING_LIGHT_DARK : TOKENS.WARNING_LIGHT_LIGHT,
    },
    background: {
      paper: isDark ? TOKENS.BG_PAPER_DARK : TOKENS.BG_PAPER_LIGHT,
      default: isDark ? TOKENS.BG_DEFAULT_DARK : TOKENS.BG_DEFAULT_LIGHT,
    },
  } as const;
};

// Custom Vars Strategies
const buildCustomTokens = (mode: PaletteMode, palette: ReturnType<typeof buildMuiPalette>) => {
  const isDark = mode === 'dark';

  return {
    // Bind to MUI palette where it makes sense (keeps it in sync on mode change)
    avatarColor: TOKENS.BG_PAPER_LIGHT,
    headerColor: palette.primary.light,

    // Strategy: in dark use primary.light, in light use a flat token for contrast
    tableHeaderBackgroundColor: isDark ? palette.primary.light : TOKENS.TABLE_HEADER_BG_LIGHT,
    pdfTableHeaderBackgroundColor: TOKENS.PRIMARY_LIGHT_LIGHT,
    pdfTableHeaderColor: TOKENS.PRIMARY_LIGHT_LIGHT_TEXT,

    // Sizes from TOKENS (don’t change by mode)
    tableFontSizePx: TOKENS.TABLE_FONT_SIZE_PX,
    tableHeaderFontSizePx: TOKENS.TABLE_HEADER_FONT_SIZE_PX,

    // Error map flatly sourced from TOKENS (stable across modes)
    concDetErrorType: {
      EXISTS_IN_FV_AND_NOT_IN_FISCAL: TOKENS.CONCDET_EXISTS_IN_FV_NOT_IN_FISCAL,
      EXISTS_IN_FISCAL_AND_NOT_IN_FV: TOKENS.CONCDET_EXISTS_IN_FISCAL_NOT_IN_FV,
      DIFFERENTS_STATUS: TOKENS.CONCDET_DIFFERENTS_STATUS,
      DIFFERENTS_CNPJ: TOKENS.CONCDET_DIFFERENTS_CNPJ,
      DIFFERENTS_AMOUNTS: TOKENS.CONCDET_DIFFERENTS_AMOUNTS,
      DIFFERENTS_POS_AMOUNT: TOKENS.CONCDET_DIFFERENTS_POS_AMOUNT,
      POS_NOT_IN_FV: TOKENS.CONCDET_POS_NOT_IN_FV,
      DIFFERENTS_POS_GCS: TOKENS.CONCDET_DIFFERENTS_POS_GCS,
      POS_NOT_IN_FISCAL: TOKENS.CONCDET_POS_NOT_IN_FISCAL,
    },

    progressBarBackground: isDark ? grey[600] : grey[300],
    // strategy to status in column status
    status: {
      ok: {
        bg: isDark ? TOKENS.STATUS_OK_BG_DARK : TOKENS.STATUS_OK_BG_LIGHT,
        fg: isDark ? TOKENS.STATUS_OK_FG_DARK : TOKENS.STATUS_OK_FG_LIGHT,
      },
      error: {
        bg: isDark ? TOKENS.STATUS_ERR_BG_DARK : TOKENS.STATUS_ERR_BG_LIGHT,
        fg: isDark ? TOKENS.STATUS_ERR_FG_DARK : TOKENS.STATUS_ERR_FG_LIGHT,
      },
    },

    pagination: {
      active: isDark ? TOKENS.PAGE_ACTIVE_DARK : TOKENS.PRIMARY_MAIN,
    },
  } as const;
};

const buildOverrideComponents = (
  mode: PaletteMode,
  palette: ReturnType<typeof buildMuiPalette>,
  custom: ReturnType<typeof buildCustomTokens>
) => {
  const isDark = mode === 'dark';

  // reglas reutilizables para el fieldset (borde) del outlined
  const outlineNoBorder = {
    '&& .MuiOutlinedInput-notchedOutline': { border: 0 },
    '&&:hover .MuiOutlinedInput-notchedOutline': { border: 0 },
    '&&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 },
    '&&.Mui-disabled .MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const outlineWithBorder = {
    '&& .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1,
    },
    '&&:hover .MuiOutlinedInput-notchedOutline': {},
    '&&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.primary.main,
      borderWidth: 2,
    },
    '&&.Mui-disabled .MuiOutlinedInput-notchedOutline': {},
  };
  return {
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            backgroundColor: isDark ? palette.primary.light : palette.primary.main,
            color: custom.avatarColor,
            border: `1px solid ${palette.primary.main}`,
            '&.Mui-disabled': {
              border: `1px solid transparent`,
            },
          },
          containedSecondary: {
            backgroundColor: isDark ? palette.background.default : palette.background.paper,
            color: isDark ? custom.avatarColor : palette.primary.main,
            border: `1px solid ${palette.primary.main}`,
            '&.Mui-disabled': {
              border: `1px solid transparent`,
            },
          },
        },
      },
      MuiAppBar: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiList: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiDialog: { styleOverrides: { paper: { backgroundImage: 'none' } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },

      // Que todo TextField / FormControl / Select sea outlined por defecto
      MuiTextField: { defaultProps: { variant: 'outlined' as const, size: 'small' as const } },
      MuiFormControl: { defaultProps: { variant: 'outlined' as const, size: 'small' as const } },
      MuiSelect: { defaultProps: { variant: 'outlined' as const } },
      /// input común (Select/Autocomplete/DatePicker usan OutlinedInput)
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            borderRadius: 8,
            ...(isDark ? outlineWithBorder : outlineNoBorder),
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          slotProps: { textField: { fullWidth: true } },
        },
      },
    },
  };
};

const themeFactory = (mode: PaletteMode, locale: string) => {
  const palette = buildMuiPalette(mode);
  const custom = buildCustomTokens(mode, palette);
  const overrides = buildOverrideComponents(mode, palette, custom);
  const base = createTheme({
    palette: {
      mode,
      ...buildMuiPalette(mode),
    },
  });

  return createTheme(
    { ...base, palette: { ...palette, ...base.palette, mode }, custom, ...overrides },
    ...muiLocal[locale as SupportedLanguages]
  );
};

export default themeFactory;
